import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-sales-invoice-template',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="invoice-container" [class.pdf-mode]="forPdf">
      <div class="invoice-header">
        <div class="company-brand">
          <h1>SALES INVOICE</h1>
          <p>{{ senderCompany }}</p>
          <p>{{ senderAddressLine }}</p>
        </div>
        <div class="invoice-meta">
          <div class="meta-group"><label>Invoice #</label><span>{{ invoice?.invoiceNumber }}</span></div>
          <div class="meta-group" *ngIf="!invoice?.isCombined"><label>Date</label><span>{{ invoice?.invoiceDate | date:'mediumDate' }}</span></div>
        </div>
      </div>

      <div class="invoice-body">
        <div class="bill-to">
          <h3>Bill To:</h3>
          <p><strong>{{ invoice?.company?.company }}</strong></p>
          <p>{{ invoice?.company?.pocName }}</p>
          <p>{{ invoice?.company?.email }}</p>
          <p>{{ invoice?.company?.phone }}</p>
          <p *ngIf="invoice?.company?.address">{{ invoice?.company?.address }}</p>
        </div>
        
        <div class="fedex-info" *ngIf="invoice?.company?.fedexNumber">
          <strong>Fedex #:</strong> {{ invoice?.company?.fedexNumber }}
        </div>

        <table class="invoice-table">
          <thead>
            <tr>
              <th *ngIf="invoice?.isCombined">Date</th>
              <th>Item</th>
              <th class="text-center">Quantity</th>
              <th class="text-right">Unit Price ($)</th>
              <th class="text-right">Amount ($)</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of invoice?.items" class="print-row">
              <td *ngIf="invoice?.isCombined">{{ item.invoiceDate | date:'shortDate' }}</td>
              <td>{{ item.item }}</td>
              <td class="text-center">{{ item.quantity }}</td>
              <td class="text-right">{{ item.unitPrice | currency }}</td>
              <td class="text-right">{{ item.amount | currency }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td [colSpan]="invoice?.isCombined ? 4 : 3" class="text-right">Total Due</td>
              <td class="text-right total-due-cell">{{ invoice?.totalDue | currency }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  `,
  styles: [`
    .invoice-container { background: white; font-size: 14px; }
    .invoice-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; border-bottom: 2px solid #f1f5f9; padding-bottom: 20px; }
    .company-brand h1 { margin: 0; color: #2563eb; letter-spacing: 2px; font-size: 1.8rem; }
    .company-brand p { margin: 4px 0 0; color: #64748b; font-size: 0.9rem; }
    .invoice-meta { text-align: right; }
    .meta-group { margin-bottom: 8px; }
    .meta-group label { font-weight: 600; color: #64748b; margin-right: 10px; }
    .meta-group span { font-weight: 500; color: #1e293b; }
    .bill-to { margin-bottom: 30px; }
    .bill-to h3 { font-size: 0.9rem; text-transform: uppercase; color: #64748b; margin-bottom: 8px; }
    .bill-to p { margin: 2px 0; color: #334155; }
    .invoice-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
    .invoice-table th { text-align: left; padding: 12px; background: #f8fafc; color: #475569; font-weight: 600; border-bottom: 2px solid #e2e8f0; }
    .invoice-table th:first-child { width: 40%; }
    .invoice-table td { padding: 12px; border-bottom: 1px solid #f1f5f9; color: #334155; }
    .invoice-table tfoot td { font-weight: 600; color: #475569; border-bottom: none; }
    .invoice-table tfoot .total-row td { font-size: 1.1rem; color: #1e293b; border-top: 2px solid #e2e8f0; padding-top: 16px; }
    .total-due-cell { color: #2563eb; }
    .text-center { text-align: center !important; }
    .text-right { text-align: right !important; }
    .print-row { page-break-inside: avoid; break-inside: avoid; }
    .fedex-info { margin-bottom: 12px; color: #334155; font-size: 0.95rem; }
    
    /* PDF specific overrides */
    .pdf-mode { page-break-inside: avoid; }
    .pdf-mode .invoice-header { margin-bottom: 20px; padding-bottom: 15px; }
    .pdf-mode .company-brand h1 { font-size: 18px; margin-bottom: 5px; }
    .pdf-mode .company-brand p { font-size: 10px; margin-top: 2px; }
    .pdf-mode .meta-group { margin-bottom: 5px; }
    .pdf-mode .meta-group label { font-size: 10px; margin-right: 5px; }
    .pdf-mode .meta-group span { font-size: 10px; }
    .pdf-mode .bill-to { margin-bottom: 20px; }
    .pdf-mode .bill-to h3 { font-size: 10px; }
    .pdf-mode .bill-to p { font-size: 10px; margin: 3px 0; }
    .pdf-mode .fedex-info { font-size: 10px; margin-bottom: 8px; }
    .pdf-mode .invoice-table th, .pdf-mode .invoice-table td { padding: 8px; font-size: 10px; }
    .pdf-mode .invoice-table tfoot .total-row td { font-size: 11px; padding-top: 10px; }
  `]
})
export class SalesInvoiceTemplateComponent {
  @Input() invoice: any;
  @Input() forPdf: boolean = false;

  get companyName(): string {
    if (this.invoice?.sender?.company) {
      return this.invoice.sender.company;
    }
    const brand = (this.invoice?.company?.brand || '').trim();
    return brand === 'K-Photo Lavie' ? 'KCE Group' : 'LFC Franchise Inc.';
  }

  get senderCompany(): string {
    return this.invoice?.sender?.company || this.companyName;
  }

  get senderAddressLine(): string {
    if (this.invoice?.sender) {
      let line = this.invoice.sender.address || '';
      if (this.invoice.sender.phone) {
        line += ' ' + this.invoice.sender.phone;
      }
      return line.trim();
    }
    return '6980 Beach Blvd., H-110, Buena Park, CA 90621 (714) 349-8856';
  }
}